import { useMutation, useQuery } from '@tanstack/react-query'
import { pb } from '@/lib/pocketbase'
import type { RfqForm, RfqFormItem } from '@/lib/pocketbase'
import { useRfqJobsStore } from '@/stores/rfqJobs'

function normalizeRfqForm(form: any): RfqForm {
  return {
    ...form,
    tender_id: form.tender || form.tender_id,
    budget_amount: form.budget_amount ?? form.budget_amount_satang,
    ref_amount: form.ref_amount ?? form.ref_amount_satang,
    expand: { ...(form as any).expand, tender_id: (form as any).expand?.tender },
  }
}

function normalizeRfqItems(items: any[]): RfqFormItem[] {
  return items.map((item, index) => ({
    ...item,
    form_id: item.form_id || item.rfq_form,
    line_number: item.line_number ?? index + 1,
    quantity: item.quantity ?? item.qty,
    unit_price: item.unit_price ?? item.unit_price_satang,
    line_total: item.line_total ?? item.line_total_satang,
  }))
}

interface GenerateTenderRfqPayload {
  tenderId: string
  model?: string
  force?: boolean
  maxAttachments?: number
  attachmentIds?: string[]
}

interface GenerateTenderRfqResponse {
  tenderId: string
  rfqFormId: string
  model: string
  itemCount: number
  usedAttachmentCount: number
}

interface GenerateTenderRfqAsyncResponse {
  jobId: string
  status: string
  tenderId: string
  model: string
  createdAt: string
}

interface GenerateTenderRfqJobError {
  code?: string
  message?: string
  details?: unknown
}

export interface GenerateTenderRfqJobStatusResponse {
  jobId: string
  status: 'queued' | 'running' | 'succeeded' | 'failed'
  tenderId: string
  model: string
  force: boolean
  maxAttachments: number
  attachmentIds: string[]
  createdAt: string
  updatedAt: string
  startedAt?: string
  completedAt?: string
  traceId?: string
  result?: GenerateTenderRfqResponse
  error?: GenerateTenderRfqJobError
}

function shouldKeepPollingRfq(
  data: { form?: { updated?: string } } | null | undefined,
  thresholdRaw?: string
) {
  if (!thresholdRaw) return false
  const threshold = Date.parse(thresholdRaw)
  if (Number.isNaN(threshold)) return false
  const updatedRaw = data?.form?.updated
  if (!updatedRaw) return true
  const updatedAt = Date.parse(updatedRaw)
  if (Number.isNaN(updatedAt)) return true
  return updatedAt < threshold
}

// Fetch RFQ form for a tender
export function useTenderRfq(
  tenderId: string,
  options?: {
    pollUntilUpdatedAfter?: string
  }
) {
  return useQuery({
    queryKey: ['tender-rfq', tenderId],
    queryFn: async () => {
      try {
        const rfqForm = await pb.collection('rfq_forms').getFirstListItem<RfqForm>(
          `tender = "${tenderId}"`
        )

        // Get items
        const items = await pb.collection('rfq_form_items').getList<RfqFormItem>(1, 100, {
          filter: `rfq_form = "${rfqForm.id}"`,
          sort: 'created',
        })

        return {
          form: normalizeRfqForm(rfqForm as any),
          items: normalizeRfqItems(items.items as any[]),
        }
      } catch {
        return null
      }
    },
    enabled: !!tenderId,
    refetchInterval: (query) =>
      shouldKeepPollingRfq(
        query.state.data as { form?: { updated?: string } } | null | undefined,
        options?.pollUntilUpdatedAfter
      )
        ? 2500
        : false,
    refetchIntervalInBackground: true,
  })
}

// Fetch all RFQ forms (Pro ops)
export function useRfqForms(filters?: {
  search?: string
  page?: number
  perPage?: number
}) {
  const { search, page = 1, perPage = 20 } = filters || {}

  return useQuery({
    queryKey: ['rfq-forms', filters],
    queryFn: async () => {
      let filter = ''
      if (search) {
        filter = `tender.title_th ~ "${search}" || tender.egp_project_no ~ "${search}"`
      }

      const result = await pb.collection('rfq_forms').getList<RfqForm>(page, perPage, {
        filter,
        expand: 'tender',
        sort: '-request_date',
      })
      return {
        ...result,
        items: result.items.map((item) => normalizeRfqForm(item as any)),
      }
    },
  })
}

// Fetch RFQ form items
export function useRfqFormItems(formId: string) {
  return useQuery({
    queryKey: ['rfq-form-items', formId],
    queryFn: async () => {
      const result = await pb.collection('rfq_form_items').getList<RfqFormItem>(1, 500, {
        filter: `rfq_form = "${formId}"`,
        sort: 'created',
      })
      return normalizeRfqItems(result.items as any[])
    },
    enabled: !!formId,
  })
}

// Generate RFQ on-demand for a specific tender
export function useGenerateTenderRfq() {
  return useMutation({
    mutationFn: async (payload: GenerateTenderRfqPayload) => {
      if (!payload.tenderId) {
        throw new Error('tenderId is required')
      }

      return pb.send<GenerateTenderRfqAsyncResponse>('/api/rfq/generate-async', {
        method: 'POST',
        body: payload,
      })
    },
  })
}

export function useGenerateTenderRfqJob(jobId?: string) {
  return useQuery({
    queryKey: ['rfq-generate-job', jobId],
    queryFn: async () => {
      const data = await pb.send<GenerateTenderRfqJobStatusResponse>(
        `/api/rfq/jobs/${encodeURIComponent(jobId || '')}`,
        {
          method: 'GET',
        }
      )
      useRfqJobsStore.getState().upsertJobStatus({
        jobId: data.jobId,
        tenderId: data.tenderId,
        status: data.status,
        model: data.model,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        startedAt: data.startedAt,
        completedAt: data.completedAt,
        traceId: data.traceId,
        error: data.error,
      })
      return data
    },
    enabled: !!jobId,
    retry: false,
    refetchInterval: (query) => {
      const status = (query.state.data as GenerateTenderRfqJobStatusResponse | undefined)?.status
      if (status === 'queued' || status === 'running') {
        return 2500
      }
      return false
    },
    refetchIntervalInBackground: true,
  })
}
